import { createRouter, createWebHistory, type LocationQuery } from 'vue-router';

import qs from 'qs';

import * as Sentry from '@sentry/vue';

import {
  fetchCachedData,
  getIsMsalInteractiveInProgress,
  refreshTokenIfNeeded
} from '@/plugins/msal';

import { $mxbFetch } from '@/utils/fetch';
import { TokenManager } from '@/utils/token_manager';
import { addNavigationBreadcrumb } from '@/utils/sentry-context';
import { getEnvironment } from '@/utils/environment';

const router = createRouter({
  parseQuery(query): LocationQuery {
    const parsed = qs.parse(query, { arrayLimit: Infinity });
    const result: LocationQuery = {};

    for (const [key, value] of Object.entries(parsed)) {
      if (value !== undefined) {
        result[key] = value as string | string[];
      }
    }

    return result;
  },
  stringifyQuery(query) {
    const result = qs.stringify(query, {
      arrayFormat: 'repeat'
    });
    return result;
  },
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  },
  routes: [
    {
      path: '/',
      name: 'home',
      component: () => import('@/views/HomeView.vue')
    },
    {
      path: '/:viewType/:viewName',
      name: 'mxb-view',
      component: () => import('@/views/MxBView.vue')
    },
    {
      path: '/invoices/:invoiceId',
      name: 'invoice-view',
      component: () => import('@/views/InvoiceView.vue')
    },
    {
      path: '/customers/:customerId',
      name: 'customer-view',
      component: () => import('@/views/CustomerView.vue')
    },
    {
      path: '/dashboards/:reportId',
      name: 'power-bi-report',
      component: () => import('@/views/PowerBiView.vue')
    },
    {
      path: '/favorites',
      name: 'favorites',
      component: () => import('@/views/FavoritesView.vue')
    },
    {
      path: '/subscriptions',
      name: 'subscriptions',
      component: () => import('@/views/SubscriptionsView.vue')
    },
    {
      path: '/applications',
      name: 'applications',
      component: () => import('@/views/ApplicationsView.vue')
    },
    {
      path: '/design-request-app',
      name: 'design-request-app',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Design Request App',
          url: 'https://bid-request-project.azurewebsites.net?organization=${user?.organizationName}&user=${user?.email}',
          permission: {
            page: 'design_request_app',
            permission: 'read'
          },
          redirectOnUnauthorized: '/'
        }
      }
    },
    {
      path: '/project-voting',
      name: 'project-voting',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Project Voting',
          url: 'https://apps.powerapps.com/play/e/default-3ec551bf-1bde-4bce-8c45-1c61329a46ec/a/f6527c97-c4d1-4897-9e5a-f4a786db054f',
          permission: {
            page: 'project_voting',
            permission: 'read'
          },
          redirectOnUnauthorized: '/'
        }
      }
    },
    {
      path: '/implementation-checklist',
      name: 'implementation-checklist',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Implementation Checklist',
          url: 'https://apps.powerapps.com/play/e/default-3ec551bf-1bde-4bce-8c45-1c61329a46ec/a/59efc206-2d40-4702-b7cd-8763924a9531?tenantId=3ec551bf-1bde-4bce-8c45-1c61329a46ec&sourcetime=1721413745213',
          permission: {
            page: 'implementation_checklist',
            permission: 'read'
          },
          redirectOnUnauthorized: '/'
        }
      }
    },
    {
      path: '/automation-panel',
      name: 'automation-panel',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Automation Panel',
          url: `https://eit-automation-panel.azurewebsites.net?embedded=true&organization_id=\${user?.organizationName}&environment=${getEnvironment()}`,
          permission: {
            page: 'automation_panel',
            permission: 'read'
          },
          redirectOnUnauthorized: '/forbidden'
        }
      }
    },
    {
      path: '/migrationToolView',
      name: 'migrationToolView',
      component: () => import('@/views/MigrationToolView.vue')
    },
    {
      path: '/viewGenerationTool',
      name: 'viewGenerationTool',
      component: () => import('@/views/DynamicGenerationView.vue')
    },
    {
      path: '/mxs',
      name: 'mxs',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Matrix For Security',
          url: 'https://dev.security.empactit.com/applications'
        }
      }
    },
    {
      path: '/indicators',
      name: 'indicators',
      component: () => import('@/views/IndicatorsView.vue')
    },
    {
      path: '/mxlMonitoring',
      name: 'mxlMonitoring',
      component: () => import('@/views/IframeView.vue'),
      meta: {
        iframeConfig: {
          title: 'Mxl Monitoring',
          url: 'https://mxm.azurewebsites.net/Dashboard#',
          permission: {
            page: 'mxlMonitoring',
            permission: 'read'
          },
          redirectOnUnauthorized: '/forbidden'
        }
      }
    },
    {
      path: '/error/:type?',
      name: 'error',
      component: () => import('@/views/ErrorView.vue')
    },
    {
      path: '/unauthorized',
      name: 'unauthorized',
      component: () => import('@/views/ErrorView.vue'),
      beforeEnter: to => {
        to.params.type = '401';
      }
    },
    {
      path: '/forbidden',
      name: 'Forbidden',
      component: () => import('@/views/ErrorView.vue'),
      beforeEnter: to => {
        to.params.type = '403';
      }
    },
    {
      path: '/:pathMatch(.*)*',
      name: 'page-not-found',
      component: () => import('@/views/ErrorView.vue'),
      beforeEnter: to => {
        to.params.type = '404';
      }
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue')
    }
  ]
});

// Track navigation in Sentry for better error context
router.afterEach((to, from) => {
  addNavigationBreadcrumb(to.fullPath, from.fullPath);
});

export default router;

const handleStateRefresh = async () => {
  // Check if an MSAL interactive process is ongoing
  if (getIsMsalInteractiveInProgress()) {
    return;
  }
  fetchCachedData();
};

router.beforeResolve(async (to, from, next) => {
  const token = TokenManager.getCachedToken('access_token')?.token;
  const isLoginRoute = to.name === 'login';

  // If no token and not going to login, redirect to login
  if (!token && !isLoginRoute) {
    return next({ name: 'login' });
  }

  // If token exists but is expired, try to refresh it before redirecting to login
  if (token && TokenManager.isCachedTokenExpired('access_token') && !isLoginRoute) {
    try {
      const refreshed = await refreshTokenIfNeeded('access_token');
      if (refreshed) {
        return next();
      } else {
        localStorage.clear();
        return next({ name: 'login' });
      }
    } catch (err) {
      localStorage.clear();
      return next({ name: 'login' });
    }
  }

  // If valid token and trying to access login, redirect to home
  if (token && !TokenManager.isCachedTokenExpired('access_token') && isLoginRoute) {
    return next({ name: 'home' });
  }

  // Continue with drill-through logic...
  try {
    await handleStateRefresh();

    if (to.query?.is_drilling_through === 'yes') {
      // Just reset the view - filters will be applied by MxBView.vue
      await $mxbFetch(`/viewdata/reset/${to.params.viewName}?isDrillthrough=true`, {
        method: 'POST'
      });

      // Only handle reference date filters here
      if (to.query?.referenceDateFrom && to.query?.referenceDateTo) {
        await $mxbFetch(`/viewdata/filters/${to.params.viewName}?isDrillthrough=true`, {
          method: 'POST',
          body: {
            filters: [
              {
                id: 'reference_date',
                type: 'timestamp',
                index: 0,
                visible: true,
                field_id: 'reference_date',
                operation: '>=',
                data_source: '',
                filter_text: to.query.referenceDateFrom,
                header_text: 'From',
                default_filter: ''
              },
              {
                id: 'reference_date',
                type: 'timestamp',
                index: 1,
                visible: true,
                field_id: 'reference_date',
                operation: '<=',
                data_source: '',
                filter_text: to.query.referenceDateTo,
                header_text: 'To',
                default_filter: ''
              }
            ]
          }
        });
      }
    }

    next();
  } catch (error) {
    Sentry.logger.error('Route guard error', { error, log_source: 'router.beforeEach' });
    if (!isLoginRoute) {
      localStorage.clear();
      return next({ name: 'login' });
    }
    next();
  }
});
